/**
 * Runtime league Bullion play lock (admin-editable). Env is the deploy fallback until
 * GET /leagues/bullion-entry-lock loads.
 */

export type LeagueBullionEntryLockMode = "minutes" | "toss";

export type LeagueBullionEntryLockConfig = {
    mode: LeagueBullionEntryLockMode;
    minutes: number;
};

export type ResolvedLeagueBullionEntryLock = {
    mode: LeagueBullionEntryLockMode;
    minutes: number;
    windowMs: number;
    lockOnTossStatus: boolean;
    whenLabel: string;
    untilLabel: string;
    playSlipLockedMessage: string;
};

const TOSS_MODE_LOCK_WINDOW_MS = 30 * 60 * 1000;
const CACHE_TTL_MS = 30_000;

function readEnvDefaults(): LeagueBullionEntryLockConfig {
    const raw = String(process.env.LEAGUE_BULLION_ENTRY_LOCK_MODE ?? "minutes")
        .trim()
        .toLowerCase();
    const mode: LeagueBullionEntryLockMode = raw === "toss" ? "toss" : "minutes";

    const minutesRaw = parseInt(String(process.env.LEAGUE_BULLION_ENTRY_LOCK_MINUTES ?? ""), 10);
    if (Number.isFinite(minutesRaw) && minutesRaw >= 0) {
        return { mode, minutes: minutesRaw };
    }
    const msRaw = parseInt(String(process.env.LEAGUE_BULLION_ENTRY_LOCK_WINDOW_MS ?? ""), 10);
    if (Number.isFinite(msRaw) && msRaw >= 0) {
        return { mode, minutes: Math.max(0, Math.round(msRaw / 60000)) };
    }
    return { mode, minutes: 5 };
}

export function normalizeLeagueBullionEntryLockConfig(
    raw: Partial<LeagueBullionEntryLockConfig> | null | undefined,
    defaults: LeagueBullionEntryLockConfig = readEnvDefaults(),
): LeagueBullionEntryLockConfig {
    const modeRaw = String(raw?.mode ?? defaults.mode).trim().toLowerCase();
    const mode: LeagueBullionEntryLockMode = modeRaw === "toss" ? "toss" : "minutes";
    const minutesNum = Number(raw?.minutes);
    const minutes =
        Number.isFinite(minutesNum) && minutesNum >= 0
            ? Math.min(180, Math.floor(minutesNum))
            : defaults.minutes;
    return { mode, minutes };
}

export function resolveLeagueBullionEntryLock(
    config: LeagueBullionEntryLockConfig,
): ResolvedLeagueBullionEntryLock {
    const normalized = normalizeLeagueBullionEntryLockConfig(config);
    const lockOnTossStatus = normalized.mode === "toss";
    const windowMs = lockOnTossStatus
        ? TOSS_MODE_LOCK_WINDOW_MS
        : Math.max(0, normalized.minutes) * 60 * 1000;

    let whenLabel: string;
    if (normalized.mode === "toss") {
        whenLabel = "at toss";
    } else if (normalized.minutes === 1) {
        whenLabel = "1 minute before first ball";
    } else {
        whenLabel = `${normalized.minutes} minutes before first ball`;
    }

    const untilLabel = normalized.mode === "toss" ? "until toss" : `until ${whenLabel}`;
    const playSlipLockedMessage =
        normalized.mode === "toss"
            ? "Bullion entry closed at toss. A lucky dip is issued automatically if you have not entered."
            : `Bullion entry closed ${whenLabel}. A lucky dip is issued automatically if you have not entered.`;

    return {
        mode: normalized.mode,
        minutes: normalized.minutes,
        windowMs,
        lockOnTossStatus,
        whenLabel,
        untilLabel,
        playSlipLockedMessage,
    };
}

export const DEFAULT_LEAGUE_BULLION_ENTRY_LOCK = resolveLeagueBullionEntryLock(readEnvDefaults());

let runtimeResolved: ResolvedLeagueBullionEntryLock = DEFAULT_LEAGUE_BULLION_ENTRY_LOCK;
let cacheAt = 0;
let inflight: Promise<ResolvedLeagueBullionEntryLock> | null = null;
const listeners = new Set<() => void>();

export function getLeagueBullionEntryLockResolved(): ResolvedLeagueBullionEntryLock {
    return runtimeResolved;
}

export function setLeagueBullionEntryLockResolved(resolved: ResolvedLeagueBullionEntryLock): void {
    runtimeResolved = resolved;
    cacheAt = Date.now();
    for (const listener of listeners) listener();
}

export function subscribeLeagueBullionEntryLock(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

async function fetchLeagueBullionEntryLock(): Promise<ResolvedLeagueBullionEntryLock> {
    const base = String(process.env.API_URL || "").replace(/\/$/, "");
    if (!base) return DEFAULT_LEAGUE_BULLION_ENTRY_LOCK;

    try {
        const res = await fetch(`${base}/leagues/bullion-entry-lock`, { method: "GET" });
        if (!res.ok) return DEFAULT_LEAGUE_BULLION_ENTRY_LOCK;
        const data = (await res.json()) as Partial<LeagueBullionEntryLockConfig> & {
            windowMs?: number;
            lockOnTossStatus?: boolean;
            whenLabel?: string;
            untilLabel?: string;
        };
        const config = normalizeLeagueBullionEntryLockConfig({
            mode: data.mode,
            minutes: data.minutes,
        });
        return resolveLeagueBullionEntryLock(config);
    } catch {
        return DEFAULT_LEAGUE_BULLION_ENTRY_LOCK;
    }
}

/** Ensure a fresh(ish) config is loaded; safe to call from effects. */
export async function ensureLeagueBullionEntryLockConfig(
    force = false,
): Promise<ResolvedLeagueBullionEntryLock> {
    const now = Date.now();
    if (!force && cacheAt > 0 && now - cacheAt < CACHE_TTL_MS) {
        return runtimeResolved;
    }
    if (!inflight) {
        inflight = fetchLeagueBullionEntryLock()
            .then((resolved) => {
                setLeagueBullionEntryLockResolved(resolved);
                return resolved;
            })
            .finally(() => {
                inflight = null;
            });
    }
    return inflight;
}
